import { isDefined } from "@clipboard-health/util-ts";
import { isBefore, parseISO } from "date-fns";

import {
  type ProvisionalExtraWorkedTimeRequest,
  type ProvisionalExtraWorkedTimeRequestResponse,
} from "../api/useGetProvisionalExtraWorkedTimeRequest";

function hasShiftExtensionRequest(
  provisionalRequests: ProvisionalExtraWorkedTimeRequest[]
): boolean {
  return provisionalRequests.some((request) => !isDefined(request.attributes.end));
}

interface GetIsShiftEndedWithoutExtensionParams {
  isExtendShiftFeatureEnabled: boolean;
  shiftEnd: string;
  clockOutEnd?: string;
  now: Date;
  provisionalExtraWorkedTimeRequest?: ProvisionalExtraWorkedTimeRequestResponse;
}

/**
 * Returns true when the shift's scheduled end time has passed, the worker has
 * not clocked out, and no shift extension request exists. Used on the focus
 * page to freeze the timer and show the shift-ended bottom sheet.
 */
export function getIsShiftEndedWithoutExtension(
  params: GetIsShiftEndedWithoutExtensionParams
): boolean {
  const {
    isExtendShiftFeatureEnabled,
    shiftEnd,
    clockOutEnd,
    now,
    provisionalExtraWorkedTimeRequest,
  } = params;

  if (!isExtendShiftFeatureEnabled) {
    return false;
  }

  const shiftEndDate = parseISO(shiftEnd);
  const isNowPastShiftEnd = !isBefore(now, shiftEndDate);
  const isNotClockedOut = !isDefined(clockOutEnd);
  const provisionalRequests = provisionalExtraWorkedTimeRequest?.data ?? [];
  const hasExtension = hasShiftExtensionRequest(provisionalRequests);

  return isNowPastShiftEnd && isNotClockedOut && !hasExtension;
}

interface GetWasClockedOutAfterShiftEndParams {
  isExtendShiftFeatureEnabled: boolean;
  shiftEnd: string;
  clockOutEnd?: string;
  provisionalExtraWorkedTimeRequest?: ProvisionalExtraWorkedTimeRequestResponse;
}

/**
 * Returns true when the worker clocked out at or after the scheduled shift end,
 * shift extension functionality is enabled, and no shift extension request exists. Used on
 * the timesheet page to lock the clock-out time to shift.end and show the MPR
 * warning.
 */
export function getWasClockedOutAfterShiftEnd(
  params: GetWasClockedOutAfterShiftEndParams
): boolean {
  const { isExtendShiftFeatureEnabled, shiftEnd, clockOutEnd, provisionalExtraWorkedTimeRequest } =
    params;

  if (!isExtendShiftFeatureEnabled || !isDefined(clockOutEnd)) {
    return false;
  }

  const provisionalRequests = provisionalExtraWorkedTimeRequest?.data ?? [];
  if (hasShiftExtensionRequest(provisionalRequests)) {
    return false;
  }

  return !isBefore(parseISO(clockOutEnd), parseISO(shiftEnd));
}
